import { Trans } from '@lingui/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { invalidate } from '@react-three/fiber';
import { i18n } from 'i18n';
import {
  ProjectModule,
  useResetSpoolThroughputMutation
} from 'providers/api';
import { useAuthorisation } from 'providers/authorisation';
import { isPcUser } from 'utils/roles';
import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useConfirmation } from 'providers/confirm';
import AccordionSection from './components/AccordionSection';
import { BetweenNodesForm } from './components/BetweenNodesForm/BetweenNodesForm';
import DeleteNodeDialog from './components/DeleteNodeDialog/DeleteNodeDialog';
import DraggableDialog from './components/DraggableDialog/DraggableDialog';
import ImportExportDialogs from './components/ImportExportDialogs/ImportExportDialogs';
import { StringNodesForm } from './components/StringNodesForm/StringNodesForm';
import useChangePipeBetweenNodes from './hooks/useChangePipeBetweenNodes';
import useChangePipes from './hooks/useChangePipes';
import { useConnectNodes, useDisconnectNodes } from './hooks/useConnectNodes';
import useDeleteNodes from './hooks/useDeleteNodes';
import useDialogPosition from './hooks/useDialogPosition';
import useDialogResize from './hooks/useDialogResize';
import useChangeFlowDirection from './hooks/useChangeFlowDirection';
import { EditorPanelPageProps } from './types';
import { PCPipe } from '../MineModel/Pipes';
import { PCNode } from '../MineModel/Nodes';
import PCSpool from '../MineModel/Spools/PCSpool';
import { isSyntheticSpool } from '../MineModel/Spools';
import { PC_BLUE } from '../MineModel/GConstants';
import { useMarquee } from '../contexts/MarqueeContext';
import { useToolHandlers } from '../contexts/ToolHandlerContext';
import { generatePipeTypeLegend, useSceneLegend } from '../Components/SceneLegend';

const EditorPanel = ({ project, mineModel, model, mineModelQuery }: EditorPanelPageProps) => {
  const authorisationContext = useAuthorisation();
  const isAdminOrPc = authorisationContext.functions.isAdmin()
    || isPcUser(authorisationContext.state.user.roles);
  const [isAddNodeOpen, setIsAddNodeOpen] = useState(false);
  const [isAddStringOpen, setIsAddStringOpen] = useState(false);
  const [selectedPipeType, setSelectedPipeType] = useState<string>('');
  const cancelAllModesRef = useRef<() => void>(() => { });
  const [isBulkSelecting, setIsBulkSelecting] = useState(false);
  const [isTonnageSelecting, setIsTonnageSelecting] = useState(false);
  const [isTonnageBulkSelecting, setIsTonnageBulkSelecting] = useState(false);
  const [selectedTonnageSpoolIds, setSelectedTonnageSpoolIds] = useState<string[]>([]);
  const [tonnagePipeType, setTonnagePipeType] = useState<string>('');
  const [isBulkPipeSelecting, setIsBulkPipeSelecting] = useState(false);
  const marquee = useMarquee();
  const tools = useToolHandlers();
  const confirm = useConfirmation();
  const { setLegendData, setShowLegend } = useSceneLegend();
  const { dialogSize, handleResizeStart } = useDialogResize();
  const { position, updatePosition } = useDialogPosition({
    dialogSize,
    isOpen: isAddNodeOpen || isAddStringOpen,
  });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const canDeleteProtectedNodes = authorisationContext.functions.isAdmin()
    || isPcUser(authorisationContext.state.user.roles);

  const showPipeTypeVisuals = useCallback(() => {
    model.setSpoolColoursByPipe(true);
    setLegendData(generatePipeTypeLegend(model, model.recipeState));
    setShowLegend(true);
  }, [model, setLegendData, setShowLegend]);

  const {
    isSelectingNodes: isDeletingNodes,
    selectedNodes: deleteSelectedNodes,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    handleSelectForDeleteNode,
    handleDeleteNode,
    handleCancelSelection: handleCancelDeleteSelection,
    handleCloseDialog,
    deleteErrors,
    handleNodeSelect: handleDeleteNodeSelect,
    handleBulkNodeSelect,
    isDeleting,
  } = useDeleteNodes({
    model,
    project,
    mineModel,
    mineModelQuery,
    canDeleteProtectedNodes,
  });

  const resetSpoolThroughputMutation = useResetSpoolThroughputMutation(project?.entityId ?? '');

  const {
    isSelectingNodes: isConnectingNodes,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    selectedNodes: connectSelectedNodes,
    handleSelectForConnectNode,
    handleCancelSelection: handleCancelConnectSelection,
    handleNodeSelect: handleConnectNodeSelect,
  } = useConnectNodes({
    model,
    project,
  });

  const {
    isSelectingNodes: isDisconnectingNodes,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    selectedNodes: disconnectSelectedNodes,
    handleSelectForDisconnectNode,
    handleCancelSelection: handleCancelDisconnectSelection,
    handleSpoolSelect: handleDisconnectSpoolSelect,
  } = useDisconnectNodes({
    model,
    project,
  });

  const {
    isSelectingNodes: isChangingPipes,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleSelectForChangePipe,
    handleCancelSelection: handleCancelChangePipeSelection,
    handleSpoolSelect: handleChangePipeSpoolSelect,
    handleBulkSpoolsChange,
    invalidateModel,
  } = useChangePipes({
    model,
    project,
    selectedPipeType: selectedPipeType || null,
  });

  const {
    isChangingPipeBetweenNodes,
    handleSelectForChangePipeBetweenNodes,
    handleCancelSelection: handleCancelChangePipeBetweenNodesSelection,
    handleNodeSelect: handleChangePipeBetweenNodesSelect,
  } = useChangePipeBetweenNodes({
    model,
    project,
    selectedPipeType: selectedPipeType || null,
  });

  const {
    isSelectingNodes: isChangingDirection,
    directionMode,
    handleSelectForDirection,
    handleCancelSelection: handleCancelDirectionSelection,
    handleSpoolSelect: handleChangeDirectionSpoolSelect,
  } = useChangeFlowDirection({
    model,
    project,
  });

  // Throughput accrues on spools, so a reset selects spools. Only persisted spools can be sent: the
  // synthetic discharge and booster/choke twin edges never carry throughput.
  const clearTonnageSelection = useCallback(() => {
    setSelectedTonnageSpoolIds((prevSelected) => {
      prevSelected.forEach((spoolId) => {
        const spool = model.spools[spoolId] as PCSpool | undefined;
        if (spool) model.setSpoolColour(spoolId, model.resolveSpoolBaseColour(spool));
      });
      return [];
    });
    requestAnimationFrame(() => {
      invalidate();
    });
  }, [model]);

  const addTonnageSpools = useCallback((spools: PCSpool[]) => {
    setSelectedTonnageSpoolIds((prevSelected) => {
      const additions = spools
        .filter((spool) => model.spools[spool.spoolId] && !isSyntheticSpool(spool))
        .map((spool) => spool.spoolId)
        .filter((spoolId) => !prevSelected.includes(spoolId));
      additions.forEach((spoolId) => model.setSpoolColour(spoolId, PC_BLUE));
      return [...prevSelected, ...additions];
    });
    requestAnimationFrame(() => {
      invalidate();
    });
  }, [model]);

  const handleTonnageSpoolSelect = useCallback((spool: PCSpool | undefined) => {
    if (!isTonnageSelecting || !spool) return;

    const spoolInModel = model.spools[spool.spoolId] as PCSpool | undefined;
    if (!spoolInModel || isSyntheticSpool(spoolInModel)) return;

    setSelectedTonnageSpoolIds((prevSelected) => {
      const isSelected = prevSelected.includes(spool.spoolId);
      if (isSelected) {
        model.setSpoolColour(spool.spoolId, model.resolveSpoolBaseColour(spoolInModel));
        return prevSelected.filter((id) => id !== spool.spoolId);
      }
      model.setSpoolColour(spool.spoolId, PC_BLUE);
      return [...prevSelected, spool.spoolId];
    });

    requestAnimationFrame(() => {
      invalidate();
    });
  }, [isTonnageSelecting, model]);

  const editModeNodeHandler = useCallback((node: PCNode | undefined) => {
    if (!node) return;
    if (isDeletingNodes) { handleDeleteNodeSelect(node); return; }
    if (isConnectingNodes) { handleConnectNodeSelect(node); return; }
    if (isChangingPipeBetweenNodes) handleChangePipeBetweenNodesSelect(node);
  }, [
    isDeletingNodes,
    isConnectingNodes,
    isChangingPipeBetweenNodes,
    handleDeleteNodeSelect,
    handleConnectNodeSelect,
    handleChangePipeBetweenNodesSelect,
  ]);

  const isAnyEditMode = isTonnageSelecting
    || isDeletingNodes
    || isConnectingNodes
    || isDisconnectingNodes
    || isChangingPipes
    || isChangingPipeBetweenNodes
    || isChangingDirection;

  useEffect(() => {
    if (!isAnyEditMode) return undefined;
    tools.setNodeClickHandler(editModeNodeHandler);
    return () => tools.setNodeClickHandler(null);
  }, [isAnyEditMode, editModeNodeHandler, tools]);

  useEffect(() => {
    if (isAddNodeOpen || isAddStringOpen) return undefined;
    if (isChangingPipes && handleChangePipeSpoolSelect) {
      tools.setSpoolClickHandler(handleChangePipeSpoolSelect);
      return () => tools.setSpoolClickHandler(null);
    }
    if (isChangingDirection && handleChangeDirectionSpoolSelect) {
      tools.setSpoolClickHandler(handleChangeDirectionSpoolSelect);
      return () => tools.setSpoolClickHandler(null);
    }
    if (isDisconnectingNodes && handleDisconnectSpoolSelect) {
      tools.setSpoolClickHandler(handleDisconnectSpoolSelect);
      return () => tools.setSpoolClickHandler(null);
    }
    if (isTonnageSelecting) {
      tools.setSpoolClickHandler(handleTonnageSpoolSelect);
      return () => tools.setSpoolClickHandler(null);
    }
    return undefined;
  }, [
    isChangingPipes,
    isChangingDirection,
    isDisconnectingNodes,
    isTonnageSelecting,
    isAddNodeOpen,
    isAddStringOpen,
    handleChangePipeSpoolSelect,
    handleChangeDirectionSpoolSelect,
    handleDisconnectSpoolSelect,
    handleTonnageSpoolSelect,
    tools,
  ]);

  const cancelAllModes = useCallback(() => {
    if (isConnectingNodes) handleCancelConnectSelection();
    if (isDisconnectingNodes) handleCancelDisconnectSelection();
    if (isChangingPipes) handleCancelChangePipeSelection();
    if (isChangingPipeBetweenNodes) handleCancelChangePipeBetweenNodesSelection();
    if (isDeletingNodes) handleCancelDeleteSelection();
    if (isChangingDirection) handleCancelDirectionSelection();
    if (isBulkSelecting) {
      setIsBulkSelecting(false);
      marquee.setMarqueeMode(false);
    }
    if (isTonnageSelecting) {
      setIsTonnageSelecting(false);
      setIsTonnageBulkSelecting(false);
      clearTonnageSelection();
      marquee.setMarqueeMode(false);
    }
    if (isBulkPipeSelecting) {
      setIsBulkPipeSelecting(false);
      marquee.setMarqueeMode(false);
      invalidateModel();
    }
  }, [
    isConnectingNodes,
    isDisconnectingNodes,
    isChangingPipes,
    isChangingPipeBetweenNodes,
    isDeletingNodes,
    isChangingDirection,
    isBulkSelecting,
    isTonnageSelecting,
    isBulkPipeSelecting,
    handleCancelConnectSelection,
    handleCancelDisconnectSelection,
    handleCancelChangePipeSelection,
    handleCancelChangePipeBetweenNodesSelection,
    handleCancelDeleteSelection,
    handleCancelDirectionSelection,
    clearTonnageSelection,
    marquee,
    invalidateModel,
  ]);

  useEffect(() => {
    const activeModes = [
      { active: isConnectingNodes, cancel: handleCancelConnectSelection },
      { active: isDisconnectingNodes, cancel: handleCancelDisconnectSelection },
      { active: isChangingPipes, cancel: handleCancelChangePipeSelection },
      { active: isChangingPipeBetweenNodes, cancel: handleCancelChangePipeBetweenNodesSelection },
      { active: isDeletingNodes, cancel: handleCancelDeleteSelection },
      { active: isChangingDirection, cancel: handleCancelDirectionSelection },
    ].filter((mode) => mode.active);

    if (activeModes.length > 1) {
      activeModes.slice(0, -1).forEach((mode) => mode.cancel());
    }
  }, [
    isConnectingNodes,
    isDisconnectingNodes,
    isChangingPipes,
    isChangingPipeBetweenNodes,
    isDeletingNodes,
    isChangingDirection,
    handleCancelConnectSelection,
    handleCancelDisconnectSelection,
    handleCancelChangePipeSelection,
    handleCancelChangePipeBetweenNodesSelection,
    handleCancelDeleteSelection,
    handleCancelDirectionSelection,
  ]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      cancelAllModes();
    };

    window.addEventListener('keydown', handleEscape);
    return () => { window.removeEventListener('keydown', handleEscape); };
  }, [cancelAllModes]);

  useEffect(() => {
    cancelAllModesRef.current = cancelAllModes;
  }, [cancelAllModes]);

  useEffect(() => () => {
    cancelAllModesRef.current();
  }, []);

  if (!model) return null;

  const handleImportBetweenNodes = () => {
    updatePosition(window.innerWidth / 2 - 400, window.innerHeight / 2 - 300);
    setIsAddNodeOpen(true);
  };

  const handleImportNewString = () => {
    updatePosition(window.innerWidth / 2 - 400, window.innerHeight / 2 - 300);
    setIsAddStringOpen(true);
  };

  const handleResetTonnage = () => {
    if (selectedTonnageSpoolIds.length === 0) {
      return;
    }

    confirm({
      variant: 'danger',
      description: i18n._('Reset cumulative volume and dry tonnes for the selected spools?'),
    }).then(() => resetSpoolThroughputMutation.mutate(
      { projectId: project.entityId as string, spoolIds: selectedTonnageSpoolIds },
      {
        onSuccess: () => {
          setIsTonnageSelecting(false);
          setIsTonnageBulkSelecting(false);
          clearTonnageSelection();
          marquee.setMarqueeMode(false);
        },
      },
    ));
  };

  const handleConnectNode = () => {
    if (isConnectingNodes) {
      handleCancelConnectSelection();
    } else {
      cancelAllModes();
      handleSelectForConnectNode();
    }
  };

  const handleDisconnectNode = () => {
    if (isDisconnectingNodes) {
      handleCancelDisconnectSelection();
    } else {
      cancelAllModes();
      handleSelectForDisconnectNode();
    }
  };

  const handleChangePipe = () => {
    if (isChangingPipes) {
      handleCancelChangePipeSelection();
    } else {
      cancelAllModes();
      showPipeTypeVisuals();
      handleSelectForChangePipe();
    }
  };

  const handleBulkPipeSelect = () => {
    if (isBulkPipeSelecting) {
      setIsBulkPipeSelecting(false);
      marquee.setMarqueeMode(false);
      invalidateModel();
    } else {
      cancelAllModes();
      showPipeTypeVisuals();
      setIsBulkPipeSelecting(true);
      marquee.setMarqueeMode(true);
    }
  };

  const handleChangePipeBetweenNodes = () => {
    if (isChangingPipeBetweenNodes) {
      handleCancelChangePipeBetweenNodesSelection();
    } else {
      cancelAllModes();
      showPipeTypeVisuals();
      handleSelectForChangePipeBetweenNodes();
    }
  };

  const handleBulkSpoolPipeChange = useCallback((spools: PCSpool[]) => {
    if (!selectedPipeType || !model) return;
    handleBulkSpoolsChange(spools);
  }, [selectedPipeType, model, handleBulkSpoolsChange]);

  const handleDeleteNodeSelection = () => {
    if (isDeletingNodes) {
      handleCancelDeleteSelection();
    } else {
      cancelAllModes();
      if (isBulkSelecting) {
        setIsBulkSelecting(false);
        marquee.setMarqueeMode(false);
      }
      handleSelectForDeleteNode();
    }
  };

  const handleOpenDeleteDialog = () => {
    if (isBulkSelecting) {
      setIsBulkSelecting(false);
      marquee.setMarqueeMode(false);
    }
    setIsDeleteDialogOpen(true);
  };

  const handleOpenTonnageSelectDialog = () => {
    if (isTonnageBulkSelecting) {
      setIsTonnageBulkSelecting(false);
      marquee.setMarqueeMode(false);
    }
    handleResetTonnage();
  };

  const handleBulkSelect = () => {
    if (isBulkSelecting) {
      setIsBulkSelecting(false);
      marquee.setMarqueeMode(false);
    } else {
      if (!isDeletingNodes) {
        cancelAllModes();
        handleSelectForDeleteNode();
      }
      setIsBulkSelecting(true);
      marquee.setMarqueeMode(true);
    }
  };

  const handleTonnageSelect = () => {
    if (isTonnageSelecting) {
      setIsTonnageSelecting(false);
      setIsTonnageBulkSelecting(false);
      clearTonnageSelection();
      marquee.setMarqueeMode(false);
    } else {
      cancelAllModes();
      setIsTonnageSelecting(true);
      clearTonnageSelection();
      marquee.setMarqueeMode(false);
    }
  };

  const handleTonnageBulkSelect = () => {
    if (isTonnageBulkSelecting) {
      setIsTonnageBulkSelecting(false);
      marquee.setMarqueeMode(false);
    } else {
      if (!isTonnageSelecting) {
        cancelAllModes();
        setIsTonnageSelecting(true);
        clearTonnageSelection();
      }
      setIsTonnageBulkSelecting(true);
      marquee.setMarqueeMode(true);
    }
  };

  const handleAddTonnagePipeType = () => {
    if (!tonnagePipeType) return;
    const spoolsOfType = Object.values(model.spools as Record<string, PCSpool>)
      .filter((spool) => spool.pipe?.name === tonnagePipeType);
    if (!isTonnageSelecting) {
      cancelAllModes();
      setIsTonnageSelecting(true);
    }
    addTonnageSpools(spoolsOfType);
  };

  useEffect(() => {
    if (isBulkSelecting) {
      marquee.setOnNodesSelected((nodes: PCNode[]) => {
        handleBulkNodeSelect(nodes);
      });
      return;
    }

    marquee.setOnNodesSelected(null);
  }, [isBulkSelecting, handleBulkNodeSelect, marquee]);

  useEffect(() => {
    if (isTonnageBulkSelecting) {
      marquee.setOnSpoolsSelected((spools: PCSpool[]) => {
        addTonnageSpools(spools);
      });
      return;
    }

    if (isBulkPipeSelecting) {
      marquee.setOnSpoolsSelected((spools: PCSpool[]) => {
        handleBulkSpoolPipeChange(spools);
      });
      return;
    }

    marquee.setOnSpoolsSelected(null);
  }, [isTonnageBulkSelecting, isBulkPipeSelecting, addTonnageSpools, handleBulkSpoolPipeChange, marquee]);

  useEffect(() => {
    if (!isDeletingNodes && isBulkSelecting) {
      setIsBulkSelecting(false);
      marquee.setMarqueeMode(false);
    }
  }, [isDeletingNodes, isBulkSelecting, marquee]);

  useEffect(() => {
    if (!isTonnageSelecting && isTonnageBulkSelecting) {
      setIsTonnageBulkSelecting(false);
      marquee.setMarqueeMode(false);
    }
  }, [isTonnageSelecting, isTonnageBulkSelecting, marquee]);

  useEffect(() => {
    if (!selectedPipeType && isBulkPipeSelecting) {
      setIsBulkPipeSelecting(false);
      marquee.setMarqueeMode(false);
      invalidateModel();
    }
  }, [selectedPipeType, isBulkPipeSelecting, marquee, invalidateModel]);

  useEffect(() => {
    if (!isTonnageSelecting && marquee.isMarqueeMode) {
      clearTonnageSelection();
    }
  }, [isTonnageSelecting, marquee.isMarqueeMode, clearTonnageSelection]);

  const deleteCount = deleteSelectedNodes.length;
  const deleteNodeLabel = deleteCount !== 1 ? i18n._(' Nodes') : i18n._(' Node');
  const tonnageCount = selectedTonnageSpoolIds.length;
  const tonnageSpoolLabel = tonnageCount !== 1 ? i18n._(' Spools') : i18n._(' Spool');

  const renderDialog = (isOpen: boolean, closeDialog: () => void, title: string, isBetweenNodes: boolean) => {
    if (isBetweenNodes) {
      return (
        <DraggableDialog
          isOpen={isOpen}
          title={title}
          position={position}
          dialogSize={dialogSize}
          onClose={closeDialog}
          onDrag={(e, data) => {
            updatePosition(data.x, data.y);
          }}
          onResizeStart={handleResizeStart}
        >
          {model && (
            <BetweenNodesForm
              model={model}
              project={project}
              closeDialog={closeDialog}
            />
          )}
        </DraggableDialog>
      );
    }
    return (
      <DraggableDialog
        isOpen={isOpen}
        title={title}
        position={position}
        dialogSize={dialogSize}
        onClose={closeDialog}
        onDrag={(e, data) => {
          updatePosition(data.x, data.y);
        }}
        onResizeStart={handleResizeStart}
      >
        {model && (
          <StringNodesForm
            model={model}
            project={project}
            unitSystemPreference={project.unitSystemPreference}
            customUnitFields={project.customUnitFields}
            closeDialog={closeDialog}
            isAdminOrPc={isAdminOrPc}
          />
        )}
      </DraggableDialog>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Import/Export Section */}
      <AccordionSection
        title={i18n._('Import/Export')}
      >
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={() => setImportDialogOpen(true)}
        >
          <Trans id="Import from External">Import from External</Trans>
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={() => setExportDialogOpen(true)}
        >
          <Trans id="Export">Export</Trans>
        </Button>
      </AccordionSection>

      {/* Add Nodes Section */}
      <AccordionSection
        title={i18n._('Add Nodes')}
      >
        <Button variant="outlined" color="secondary" fullWidth onClick={handleImportNewString}>
          <Trans id="Add Node(s) from Coordinates">Add Node(s) from Coordinates</Trans>
        </Button>
        <Button variant="outlined" color="secondary" fullWidth onClick={handleImportBetweenNodes}>
          <Trans id="Add Node to Spool">Add Node to Spool</Trans>
        </Button>
      </AccordionSection>

      {/* Connect/Disconnect Section */}
      <AccordionSection title={i18n._('Connect/Disconnect')}>
        <Button
          variant={isConnectingNodes ? 'contained' : 'outlined'}
          color="secondary"
          fullWidth
          onClick={handleConnectNode}
        >
          {isConnectingNodes ? <Trans id="Cancel Connect">Cancel Connect</Trans> : <Trans id="Connect Nodes">Connect Nodes</Trans>}
        </Button>
        <Button
          variant={isDisconnectingNodes ? 'contained' : 'outlined'}
          color="secondary"
          fullWidth
          onClick={handleDisconnectNode}
        >
          {isDisconnectingNodes ? <Trans id="Cancel Disconnect">Cancel Disconnect</Trans> : <Trans id="Disconnect Spool">Disconnect Spool</Trans>}
        </Button>
      </AccordionSection>

      {/* Change Pipes Section */}
      <AccordionSection
        title={i18n._('Change Pipes')}
      >
        <FormControl fullWidth size="small">
          <InputLabel><Trans id="Pipe Type">Pipe Type</Trans></InputLabel>
          <Select
            value={selectedPipeType}
            label={i18n._('Pipe Type')}
            onChange={(e) => setSelectedPipeType(e.target.value as string)}
          >
            {Object.values(model.pipes as Record<string, PCPipe>).map((pipe: PCPipe) => (
              <MenuItem key={pipe.name} value={pipe.name}>
                {pipe.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          color="secondary"
          variant={isChangingPipes ? 'contained' : 'outlined'}
          fullWidth
          onClick={handleChangePipe}
          disabled={!selectedPipeType}
        >
          {isChangingPipes ? <Trans id="Cancel Change Pipe">Cancel Change Pipe</Trans> : <Trans id="Change Pipe on Spool">Change Pipe on Spool</Trans>}
        </Button>
        <Button
          color="secondary"
          variant={isBulkPipeSelecting ? 'contained' : 'outlined'}
          fullWidth
          onClick={handleBulkPipeSelect}
          disabled={!selectedPipeType}
        >
          {isBulkPipeSelecting
            ? <Trans id="Cancel Bulk Select">Cancel Bulk Select</Trans>
            : <Trans id="Bulk Select Spool">Bulk Select Spool</Trans>}
        </Button>
        <Button
          color="secondary"
          variant={isChangingPipeBetweenNodes ? 'contained' : 'outlined'}
          fullWidth
          onClick={handleChangePipeBetweenNodes}
          disabled={!selectedPipeType}
        >
          {isChangingPipeBetweenNodes
            ? <Trans id="Cancel Between Nodes">Cancel Between Nodes</Trans>
            : <Trans id="Change Between Nodes">Change Between Nodes</Trans>}
        </Button>
      </AccordionSection>

      {/* Change Direction Section */}
      <AccordionSection
        title={i18n._('Change Direction')}
        buttonLabel={'' as any}
        onButtonClick={undefined as any}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={directionMode === 'FORWARDS' && isChangingDirection ? 'contained' : 'outlined'}
            color="secondary"
            fullWidth
            onClick={() => handleSelectForDirection('FORWARDS')}
          >
            <Trans id="Forwards">Forwards</Trans>
          </Button>
          <Button
            variant={directionMode === 'BACKWARDS' && isChangingDirection ? 'contained' : 'outlined'}
            color="secondary"
            fullWidth
            onClick={() => handleSelectForDirection('BACKWARDS')}
          >
            <Trans id="Backwards">Backwards</Trans>
          </Button>
          <Button
            variant={directionMode === 'BIDIRECTIONAL' && isChangingDirection ? 'contained' : 'outlined'}
            color="secondary"
            fullWidth
            onClick={() => handleSelectForDirection('BIDIRECTIONAL')}
          >
            <Trans id="Bi-directional">Bi-directional</Trans>
          </Button>
        </Box>
      </AccordionSection>
      {/* Delete Nodes Section */}
      <AccordionSection
        title={i18n._('Delete Nodes')}
        buttonLabel={isDeletingNodes
          ? i18n._('Delete').concat(` ${deleteSelectedNodes.length} `).concat(deleteNodeLabel)
          : i18n._('Select Nodes to Delete')}
        onButtonClick={isDeletingNodes ? handleOpenDeleteDialog : handleDeleteNodeSelection}
      >
        <Button
          variant={isBulkSelecting ? 'contained' : 'outlined'}
          color="secondary"
          fullWidth
          onClick={handleBulkSelect}
        >
          {isBulkSelecting
            ? <Trans id="Cancel Bulk Select">Cancel Bulk Select</Trans>
            : <Trans id="Bulk Select Nodes">Bulk Select Nodes</Trans>}
        </Button>
        {isDeletingNodes && (
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={handleCancelDeleteSelection}
          >
            <Trans id="Cancel Selection">Cancel Selection</Trans>
          </Button>
        )}
      </AccordionSection>
      {/* Reset Throughputs (Wear Monitoring) Section */}
      {project.enabledModules?.includes(ProjectModule.WearMonitoring) && (
        <AccordionSection
          title={i18n._('Reset Throughputs (Wear Monitoring)')}
          buttonLabel={isTonnageSelecting
            ? i18n._('Reset Throughput for').concat(` ${tonnageCount} `).concat(tonnageSpoolLabel)
            : i18n._('Select Spools for Throughput Reset')}
          onButtonClick={isTonnageSelecting ? handleOpenTonnageSelectDialog : handleTonnageSelect}
        >
          <Button
            variant={isTonnageBulkSelecting ? 'contained' : 'outlined'}
            color="secondary"
            fullWidth
            onClick={handleTonnageBulkSelect}
          >
            {isTonnageBulkSelecting
              ? <Trans id="Cancel Bulk Select">Cancel Bulk Select</Trans>
              : <Trans id="Bulk Select Spools">Bulk Select Spools</Trans>}
          </Button>
          <FormControl fullWidth size="small">
            <InputLabel><Trans id="Pipe Type">Pipe Type</Trans></InputLabel>
            <Select
              value={tonnagePipeType}
              label={i18n._('Pipe Type')}
              onChange={(e) => setTonnagePipeType(e.target.value as string)}
            >
              {Object.values(model.pipes as Record<string, PCPipe>).map((pipe: PCPipe) => (
                <MenuItem key={pipe.name} value={pipe.name}>
                  {pipe.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={handleAddTonnagePipeType}
            disabled={!tonnagePipeType}
          >
            <Trans id="Select All Spools of Pipe Type">Select All Spools of Pipe Type</Trans>
          </Button>
          {isTonnageSelecting && (
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleTonnageSelect}
            >
              <Trans id="Cancel Selection">Cancel Selection</Trans>
            </Button>
          )}
        </AccordionSection>
      )}

      {renderDialog(isAddNodeOpen, () => setIsAddNodeOpen(false), i18n._('Add Node to Spool'), true)}
      {renderDialog(isAddStringOpen, () => setIsAddStringOpen(false), i18n._('Add Node(s) from Coordinates'), false)}

      <DeleteNodeDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDelete={handleDeleteNode}
        onCancel={() => {
          handleCancelDeleteSelection();
          handleCloseDialog();
        }}
        selectedNodes={deleteSelectedNodes}
        deleteErrors={deleteErrors}
        isDeleting={isDeleting}
      />

      <ImportExportDialogs
        importOpen={importDialogOpen}
        exportOpen={exportDialogOpen}
        onImportClose={() => setImportDialogOpen(false)}
        onExportClose={() => setExportDialogOpen(false)}
        onPreviewOpen={cancelAllModes}
        model={model}
        project={project}
        mineModel={mineModel}
        mineModelQuery={mineModelQuery}
        isAdminOrPc={isAdminOrPc}
      />
    </Box>
  );
};

export default EditorPanel;
